import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await authAPI.requestPasswordReset(email);
            setSubmitted(true);
        } catch (err) {
            // Always show success to prevent email enumeration
            setSubmitted(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Theme Toggle */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="flex items-center justify-center mb-8">
                        <Logo variant="full" size="large" />
                    </Link>

                    <Card className="bg-card border-border">
                        <CardHeader className="text-center">
                            <CardTitle className="font-heading font-bold text-2xl text-foreground">
                                {submitted ? 'Check Your Email' : 'Forgot Password?'}
                            </CardTitle>
                            <CardDescription>
                                {submitted 
                                    ? 'If an account exists with this email, you will receive a password reset link.'
                                    : 'Enter your email and we\'ll send you a reset link'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {submitted ? (
                                <div className="text-center space-y-6">
                                    <div className="flex justify-center">
                                        <div className="rounded-full bg-emerald-500/10 p-4">
                                            <CheckCircle className="h-12 w-12 text-emerald-500" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-muted-foreground">
                                            We sent a password reset link to:
                                        </p>
                                        <p className="font-medium text-foreground">{email}</p>
                                    </div>
                                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                                        <p className="text-sm text-amber-600 dark:text-amber-400">
                                            The link expires in 24 hours. Check your spam folder if you don't see it.
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setSubmitted(false);
                                            setEmail('');
                                        }}
                                        className="w-full"
                                        data-testid="forgot-password-try-again-btn"
                                    >
                                        Try a different email
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="contractor@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="pl-10 bg-accent border-border h-12"
                                                required
                                                data-testid="forgot-password-email-input"
                                            />
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full h-12 text-base active:scale-95 transition-transform"
                                        disabled={loading}
                                        data-testid="forgot-password-submit-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            'Send Reset Link'
                                        )}
                                    </Button>
                                </form>
                            )}

                            <div className="mt-6 text-center">
                                <Link 
                                    to="/login" 
                                    className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2"
                                    data-testid="forgot-password-back-link"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Back to Sign In
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default ForgotPassword;
